import * as React from 'react';
import { Search, X } from 'lucide-react';

export interface SearchOnPageProps {
  /**
   * Value of the search input
   */
  value?: string;
  /**
   * Callback when value changes
   */
  onChange?: (value: string) => void;
  /**
   * Callback when search is submitted
   */
  onSubmit?: (value: string) => void;
  /**
   * Callback when clear button is clicked
   */
  onClear?: () => void;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

/**
 * SearchOnPage component - Arbor Design System
 *
 * A search input with fixed 200px width for filtering content on the current page.
 * Shows green focus ring when active and X button to clear.
 */
export const SearchOnPage = React.forwardRef<HTMLInputElement, SearchOnPageProps>(
  (
    {
      value = '',
      onChange,
      onSubmit,
      onClear,
      placeholder = 'Search Reports',
      className,
      style,
      'data-testid': dataTestId,
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Merge refs
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onSubmit?.(value);
      }
    };

    const handleClear = () => {
      onChange?.('');
      onClear?.();
      inputRef.current?.focus();
    };

    const containerStyles: React.CSSProperties = {
      position: 'relative',
      width: '200px',
      height: '32px',
      backgroundColor: isFocused ? '#ffffff' : isHovered ? '#efefef' : '#ffffff',
      borderRadius: '16px',
      padding: '8px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'text',
      transition: 'all 0.2s ease-in-out',
      boxSizing: 'border-box',
      border: isFocused ? '1px solid #efefef' : 'none',
      boxShadow: isFocused ? '0px 0px 0px 3px #3cad51' : 'none',
      ...style,
    };

    const iconContainerStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      padding: '2px',
    };

    const inputStyles: React.CSSProperties = {
      border: 'none',
      outline: 'none',
      backgroundColor: 'transparent',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: '13px',
      fontWeight: 400,
      color: '#2f2f2f',
      lineHeight: '1.5',
      flex: 1,
      width: '100%',
      minWidth: 0,
    };

    const clearButtonStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: '16px',
      height: '16px',
      cursor: 'pointer',
      border: 'none',
      background: 'none',
      padding: 0,
    };

    const showClearButton = isFocused && value.length > 0;
    const iconColor = isFocused || isHovered ? '#2f2f2f' : '#595959';

    return (
      <div
        className={className}
        style={containerStyles}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => inputRef.current?.focus()}
        data-testid={dataTestId}
      >
        <div style={iconContainerStyles}>
          <Search size={12} color={iconColor} strokeWidth={2} />
        </div>

        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={isFocused ? '' : placeholder}
          style={inputStyles}
        />

        {showClearButton && (
          <button
            type="button"
            onClick={handleClear}
            style={clearButtonStyles}
            aria-label="Clear search"
          >
            <X size={13.333} color="#2f2f2f" strokeWidth={2} />
          </button>
        )}
      </div>
    );
  }
);

SearchOnPage.displayName = 'SearchOnPage';
